import React, { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { DialogContext } from "../../store/context/DialogContext";
import { doGET, doPOST } from "../../util/HttpUtil";
import {
  clearStoredAuthMessage,
  getStoredAuthMessage,
  getVerifyIndividualContext,
  hasCompleteVerifyIndividualContext,
  INVALID_VERIFY_LINK_MESSAGE,
  readAuthResume,
  syncVerifyIndividualContext,
} from "../../util/authResume";
import { handleUtmInterested } from "../../util/utm";
import { isValidEmail } from "../../util/email";
import {
  buildAutomationChildTestId,
  normalizeAutomationTestIdSegment,
} from "../../components/InputField/testId";
import LoginCredentials from "./LoginCredentials";
import "./LoginCredentials.scss";
import LoginOtp from "./LoginOtp";
export const validateEmail = isValidEmail;

const getAuthFlowKey = (form = {}) => {
  if (form?.type === "kyc") {
    return form?.completeApplication ? "complete-application" : "verify-individual";
  }

  if (form?.formType === "EDD") {
    return "periodic-review";
  }

  return form?.path || form?.formType || form?.apiPath || form?.name || "login";
};

export const getAuthTestId = (form = {}) => (
  `auth-${normalizeAutomationTestIdSegment(getAuthFlowKey(form))}-login`
);

const Auth = ({ isUser, form }) => {
  const location = useLocation();
  const currentRoute = location.pathname;
  const authTestId = getAuthTestId(form);
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [data, setData] = useState({
    authType: "otp",
    phone: "",
    language: "en",
    captchaQuery: "",
  });

  const [loading, setLoading] = useState(false);
  const { showError, showMessage } = useContext(DialogContext);
  const [step, setStep] = useState(currentRoute === "/sign-up" ? 2 : 0);

  const [captcha, setCapcha] = useState("");
  const [errorStatus, setErrorStatus] = useState(null);
  const [otpId, setOtpId] = useState("");
  const [errorMessage, setErrorMessage] = useState({
    phone: "",
    captchaQuery: "",
  });
  const [imageURL, setImageUrl] = useState();
  const [selecteToggle, setSelectedToggle] = useState(true);
  const [hasErr, setHasErr] = useState(false);
  const [shouldByPassCaptcha, setShouldByPassCaptcha] = useState(false);

  useEffect(() => {
    getShouldShowCaptcha();

    const storedResume = readAuthResume();
    const storedMessage = getStoredAuthMessage();
    if (storedMessage && (!storedResume?.loginPath || storedResume?.loginPath === currentRoute)) {
      showError(storedMessage);
      clearStoredAuthMessage();
    }
  }, [currentRoute, showError]);

  const getShouldShowCaptcha = async () => {
    try {
      const response = await doGET(`/api/sys-config/defaultOTP/detail`, true)
      if (response?.data?.defaultOTP && response?.data?.defaultOTP  != "") {
        setShouldByPassCaptcha(true);
      }
    } catch (error) {
      console.log(error);
    }
  };


  useEffect(() => {

    const currentUrl = window.location.href;
    const urlObject = new URL(currentUrl);
    const utm_referrer = urlObject.searchParams.get('utm_referrer');

    const gTagUtmData = {
      utm_source: urlObject.searchParams.get('utm_source') || '',
      utm_medium: urlObject.searchParams.get('utm_medium') || '',
      utm_campaign: urlObject.searchParams.get('utm_campaign') || '',
      utm_content: urlObject.searchParams.get('utm_content') || '',
      utm_term: urlObject.searchParams.get('utm_term') || '',
    }

    if (form?.type == "kyc") {
      const storedOrQueryVerifyContext = getVerifyIndividualContext({
        search: urlObject.search,
        allowStoredFallback: Boolean(form?.allowStoredVerifyContext),
      });
      const formVerifyContext = Object.entries(form?.verifyContext || {}).reduce((context, [key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          context[key] = value;
        }
        return context;
      }, {});
      const verifyContext = {
        ...storedOrQueryVerifyContext,
        ...formVerifyContext,
      };
      const isCompleteApplicationFlow = Boolean(form?.completeApplication);
      const hasVerifyContext = isCompleteApplicationFlow
        ? Boolean(verifyContext?.ubo_id)
        : hasCompleteVerifyIndividualContext(verifyContext);

      if (hasVerifyContext && !isCompleteApplicationFlow) {
        syncVerifyIndividualContext(verifyContext);
      } else if (hasVerifyContext && verifyContext?.ubo_id) {
        localStorage.setItem("ubo_id", verifyContext.ubo_id);
        if (verifyContext?.email) {
          localStorage.setItem("email", verifyContext.email);
        }
      } else {
        showError(INVALID_VERIFY_LINK_MESSAGE);
      }

      handleUtmInterested({ formType: form?.formType, utmPayload: gTagUtmData });

      setData((prevData) => ({
        ...prevData,
        ...(utm_referrer && { utm_referrer }),
        ...(gTagUtmData && { gTagUtmData }),
        email: verifyContext?.email,
        ubo_id: verifyContext?.ubo_id,
        application_id: verifyContext?.form_id,
        missingVerifyContext: !hasVerifyContext,
      }));
    } else {
      let emailParam = decodeURIComponent(urlObject.searchParams.get('email') ?? "") ;
      if (emailParam) {
        emailParam = emailParam.replace(/ /g, '+');
      }

      handleUtmInterested({ formType: form?.formType, utmPayload: gTagUtmData });

      setData(prevData => ({
        ...prevData,
        ...(utm_referrer && { utm_referrer }),
        ...(gTagUtmData && { gTagUtmData }),
        ...(emailParam && { email: emailParam }),
      }));
    }

  }, [form?.allowStoredVerifyContext, form?.completeApplication, form?.formType, form?.type, form?.verifyContext, showError]);


  const getOTP = async () => {
    if (!validateEmail(data?.email)) {
      setHasErr(true)
      showError("Please provide valid email");
      return;
    }
    const missingKycContext = form?.completeApplication
      ? (!data?.ubo_id || data?.missingVerifyContext)
      : (!data?.ubo_id || !data?.application_id || data?.missingVerifyContext);
    if (form?.type === "kyc" && missingKycContext) {
      setHasErr(true);
      showError(INVALID_VERIFY_LINK_MESSAGE);
      return;
    }
    if (!recaptchaToken && !shouldByPassCaptcha) {
      setErrorMessage({
        ...errorMessage,
        captchaQuery: "Please complete the captcha",
      });
      return;
    }


    const newData = {
      ...(data?.phone && !selecteToggle && { phone: data?.phone }),
      ...(data?.phone && !selecteToggle && { countryCode: data?.countryCode ? data?.countryCode : "91" }),
      ...(data?.email && selecteToggle && { email: data?.email }),
      platform: "web",
      gCaptchaResponse: recaptchaToken,
      //   captchaQuery: ,
      //   captcha_id: captcha,
      //   otp_id: otp_id
    };
    try {
      setLoading(true);
      const res = await doPOST("/auth/token", newData);
      if (res?.status === 200) {
        showMessage("One-time password successfully sent to your email", "Success");
        setOtpId(data?.email ? res?.data?.email : res?.data?.phone);
        setStep(1);
        setHasErr(false)
      }
    } catch (error) {
      setHasErr(true)
      setErrorStatus(error);
      setData((prev) => ({
        ...prev,
        captchaQuery: "",
      }));
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const isInspecting = typeof window.__REACT_DEVTOOLS_GLOBAL_HOOK__ === "object";

  return (
    <>
      <div
        className="d-flex justify-content-center row"
        data-testid={authTestId}
        style={{
          margin: "auto",
          height: isInspecting ? "100vh" : "auto",
        }}
      >

        <div className=" col-12 " data-testid={buildAutomationChildTestId(authTestId, step === 0 ? "credentials-stage" : "otp-stage")}>
          {step === 0 ? (
            <LoginCredentials
              getOTP={getOTP}
              data={data}
              hasErr={hasErr}
              setData={setData}
              loading={loading}
              form={form}
              errorMessage={errorMessage}
              imageURL={imageURL}
              selecteToggle={selecteToggle}
              shouldByPassCaptcha={shouldByPassCaptcha}
              captchaLoading={captchaLoading}
              recaptchaToken={recaptchaToken}
              setRecaptchaToken={setRecaptchaToken}
              testId={authTestId}
            />
          ) : step === 1 ? (
            <LoginOtp
              errorStatus={errorStatus}
              setErrorStatus={setErrorStatus}
              otpId={otpId}
              data={data}
              setData={setData}
              setStep={setStep}
              getOTP={getOTP}
              isUser={isUser}
              form={form}
              captcha={captcha}
              setCapcha={setCapcha}
              loading={loading}
              setLoading={setLoading}
              testId={authTestId}
            />
          ) : null}
        </div>
      </div>
    </>
  );
};

export default Auth;
